import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const CATEGORY_ORDER = ["All", "Furniture", "Lighting", "Decor", "Textiles", "Kitchen", "Home Safety"];
const PRICE_OPTIONS = [
  { label: "Any price", min: null, max: null },
  { label: "Under $100", min: 0, max: 100 },
  { label: "$100 – $500", min: 100, max: 500 },
  { label: "$500 – $1,000", min: 500, max: 1000 },
  { label: "Over $1,000", min: 1000, max: null },
];
const PAGE_SIZE = 24;

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(CATEGORY_ORDER);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [params, setParams] = useSearchParams();
  const [priceIdx, setPriceIdx] = useState(() => (params.get("maxprice") ? 1 : 0));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const category = params.get("category") || "All";
  const sub = params.get("sub") || "";
  const search = params.get("search") || "";

  useEffect(() => {
    api.get("/products").then((r) => {
      const present = new Set(r.data.map((p) => p.category));
      setCategories(CATEGORY_ORDER.filter((c) => c === "All" || present.has(c)));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setVisible(PAGE_SIZE);
    const apiParams = {};
    if (category !== "All") apiParams.category = category;
    if (sub) apiParams.sub = sub;
    if (search) apiParams.q = search;
    api
      .get("/products", { params: apiParams })
      .then((r) => setProducts(r.data))
      .catch(() => { setProducts([]); setError(true); })
      .finally(() => setLoading(false));
  }, [category, sub, search, retry]);

  const { min, max } = PRICE_OPTIONS[priceIdx];
  const filtered = products.filter((p) => (min == null || p.price >= min) && (max == null || p.price < max));
  const shown = filtered.slice(0, visible);

  return (
    <main data-testid="shop-page" className="pt-24">
      <div className="mx-auto max-w-[1600px] px-6 py-16 sm:px-10 sm:py-24">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-taupe">The full catalogue</p>
          <h1 className="font-serif text-4xl font-light tracking-tighter sm:text-5xl lg:text-6xl">
            Shop
          </h1>
        </Reveal>
        <div className="mt-12 flex flex-wrap items-center gap-4" data-testid="shop-filters">
          <span className="text-xs uppercase tracking-[0.2em] text-ash">Browse</span>
          <div className="relative">
            <select
              data-testid="category-dropdown"
              value={category}
              onChange={(e) => setParams(e.target.value === "All" ? {} : { category: e.target.value })}
              className="appearance-none rounded-full border border-charcoal/20 bg-transparent py-2.5 pl-6 pr-12 text-xs uppercase tracking-[0.2em] text-charcoal outline-none transition-colors duration-300 hover:border-charcoal focus:border-charcoal"
            >
              {categories.map((c) => (
                <option key={c} value={c} data-testid={`category-option-${c.toLowerCase()}`}>
                  {c === "All" ? "All pieces" : c}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-ash" />
          </div>
          <div className="relative">
            <select
              data-testid="price-filter-dropdown"
              value={priceIdx}
              onChange={(e) => { setPriceIdx(Number(e.target.value)); setVisible(PAGE_SIZE); }}
              className="appearance-none rounded-full border border-charcoal/20 bg-transparent py-2.5 pl-6 pr-12 text-xs uppercase tracking-[0.2em] text-charcoal outline-none transition-colors duration-300 hover:border-charcoal focus:border-charcoal"
            >
              {PRICE_OPTIONS.map((o, i) => (
                <option key={o.label} value={i} data-testid={`price-option-${i}`}>{o.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-ash" />
          </div>
          {search && (
            <span data-testid="shop-search-chip" className="rounded-full bg-moss px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-bone">
              Search: &ldquo;{search}&rdquo;
            </span>
          )}
          {sub && (
            <span data-testid="shop-subcategory-chip" className="rounded-full bg-charcoal px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-bone">
              {category} → {sub}
            </span>
          )}
          {!loading && (
            <span data-testid="shop-count" className="text-xs uppercase tracking-[0.2em] text-ash">
              {filtered.length} {filtered.length === 1 ? "piece" : "pieces"}
            </span>
          )}
        </div>
        {loading ? (
          <p data-testid="shop-loading" className="mt-24 text-sm font-light text-ash">Gathering the collection...</p>
        ) : error ? (
          <div data-testid="shop-error" className="mt-24 text-center">
            <p className="text-sm font-light text-ash">The collection didn't load — the shop may be waking up.</p>
            <button
              data-testid="shop-retry-button"
              onClick={() => setRetry((r) => r + 1)}
              className="mt-6 rounded-full border border-charcoal/20 px-10 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-charcoal hover:text-bone"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="mt-16 grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-10 lg:grid-cols-4">
              {shown.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 4) * 0.05}>
                  <ProductCard product={p} ratio="aspect-[4/5]" />
                </Reveal>
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-20 text-center">
                <button
                  data-testid="load-more-button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="rounded-full border border-charcoal/20 px-10 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-charcoal hover:text-bone"
                >
                  Load more — {filtered.length - visible} remaining
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
